/**
 * Page Floating Images — Y parallax via GSAP ScrollTrigger.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  PARALLAX ANPASSEN → PARALLAX_TRAVEL_* und PARALLAX_SCROLL unten.
 *  Viewport-Buckets: phone (<768) | tablet (768–1023) | desktop (≥1024)
 *  Rebuild nur bei Bucket-Wechsel, nicht bei Mobile-Browser-Höhe.
 * ═══════════════════════════════════════════════════════════════════
 */
(function () {
  const GSAP_SRC = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  const SCROLL_TRIGGER_SRC =
    'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';

  const VIEWPORT_MQ = {
    phone: '(max-width: 767px)',
    tablet: '(min-width: 768px) and (max-width: 1023px)',
    desktop: '(min-width: 1024px)',
  };

  /** @type {Record<number, number>} Y-Travel in px — Tablet + Desktop */
  const PARALLAX_TRAVEL_DESKTOP = {
    1: 70,
    2: 130,
    3: 20,
    4: 220,
    5: 50,
  };

  /** @type {Record<number, number>} Y-Travel in px — Phone */
  const PARALLAX_TRAVEL_MOBILE = {
    1: 20,
    2: 100,
    3: 40,
    4: 150,
    5: 20,
  };

  const PARALLAX_SCROLL = {
    desktop: {
      travelStartFactor: 0.2,
      scrubY: 0.6,
      scrollStartY: 'top 50%',
    },
    mobile: {
      travelStartFactor: 0.08,
      scrubY: 1.4,
      scrollStartY: 'top 70%',
    },
  };

  let gsapReady;
  let scrollTriggerMobileResizeConfigured = false;

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function ensureGsap() {
    if (gsapReady) return gsapReady;
    gsapReady = loadScript(GSAP_SRC)
      .then(() => loadScript(SCROLL_TRIGGER_SRC))
      .then(() => {
        window.gsap.registerPlugin(window.ScrollTrigger);
        if (!scrollTriggerMobileResizeConfigured) {
          window.ScrollTrigger.config({ ignoreMobileResize: true });
          scrollTriggerMobileResizeConfigured = true;
        }
      });
    return gsapReady;
  }

  function getViewportBucket() {
    if (window.matchMedia(VIEWPORT_MQ.phone).matches) return 'phone';
    if (window.matchMedia(VIEWPORT_MQ.tablet).matches) return 'tablet';
    return 'desktop';
  }

  function isPhoneBucket(bucket) {
    return bucket === 'phone';
  }

  function getFloatIndex(el) {
    const match = el.className.match(/page-floating-imgs__float--(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }

  function getTravelForFloat(el, bucket) {
    const viewport = bucket ?? getViewportBucket();
    const index = getFloatIndex(el);
    const table = isPhoneBucket(viewport)
      ? PARALLAX_TRAVEL_MOBILE
      : PARALLAX_TRAVEL_DESKTOP;
    return table[index] ?? table[1];
  }

  function getScrollConfig(bucket) {
    const viewport = bucket ?? getViewportBucket();
    return isPhoneBucket(viewport) ? PARALLAX_SCROLL.mobile : PARALLAX_SCROLL.desktop;
  }

  function debounce(fn, delay) {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function destroySectionAnimations(section) {
    if (section._floatingImgsTweens) {
      section._floatingImgsTweens.forEach((tween) => {
        tween.scrollTrigger?.kill();
        tween.kill();
      });
      delete section._floatingImgsTweens;
    }

    const { gsap } = window;
    if (gsap) {
      gsap.set(section.querySelectorAll('.page-floating-imgs__float'), {
        clearProps: 'transform',
      });
    }
  }

  function buildParallax(section) {
    const { gsap } = window;
    const floats = section.querySelectorAll('.page-floating-imgs__float');
    if (!floats.length || !gsap) return;

    destroySectionAnimations(section);

    const bucket = getViewportBucket();
    const scrollConfig = getScrollConfig(bucket);
    const tweens = [];

    floats.forEach((el) => {
      const travel = getTravelForFloat(el, bucket);

      const tweenY = gsap.fromTo(
        el,
        { y: travel * scrollConfig.travelStartFactor },
        {
          y: -travel,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: scrollConfig.scrollStartY,
            end: 'bottom top',
            scrub: scrollConfig.scrubY,
          },
        }
      );

      tweens.push(tweenY);
    });

    section._floatingImgsTweens = tweens;
    section._floatingImgsViewportBucket = bucket;
    window.ScrollTrigger?.refresh();
  }

  function refreshSection(section) {
    if (section.dataset.parallaxInit !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    ensureGsap().then(() => buildParallax(section));
  }

  function handleViewportResize(section) {
    const bucket = getViewportBucket();
    if (bucket === section._floatingImgsViewportBucket) return;

    refreshSection(section);
  }

  function initSection(section) {
    if (section.dataset.parallaxInit === 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const floats = section.querySelectorAll('.page-floating-imgs__float');
    if (!floats.length) return;

    section.dataset.parallaxInit = 'true';

    ensureGsap()
      .then(() => {
        buildParallax(section);

        const onResize = debounce(() => handleViewportResize(section), 150);
        section._floatingImgsResizeHandler = onResize;
        window.addEventListener('resize', onResize);
      })
      .catch(() => {
        section.dataset.parallaxInit = 'false';
      });
  }

  function destroySection(section) {
    destroySectionAnimations(section);

    if (section._floatingImgsResizeHandler) {
      window.removeEventListener('resize', section._floatingImgsResizeHandler);
      delete section._floatingImgsResizeHandler;
    }

    delete section._floatingImgsViewportBucket;
    section.dataset.parallaxInit = 'false';
  }

  function initAll(root = document) {
    root.querySelectorAll('.page-floating-imgs').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector?.('.page-floating-imgs') || event.target;
    if (section?.classList?.contains('page-floating-imgs')) {
      initSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector?.('.page-floating-imgs') || event.target;
    if (section?.classList?.contains('page-floating-imgs')) {
      destroySection(section);
    }
  });
})();
